import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFileUpload } from './useFileUpload';
import { findRestrictedMessageContent } from '@/lib/messageSafety';
import { consumeLimitedAction } from '@/lib/usageLimits';
import { extractStorageObjectPath } from '@/lib/storageUrls';
import { fetchDirectoryProfileMap, fetchMessageProfileMap, uniqueIds } from '@/lib/supabase-joins';
import { canCreateDealForProperty } from '@/lib/deals';
import { canDeleteDocument } from '@/lib/documents';
import { isVoiceNoteAttachment } from '@/lib/messages/voiceNotes';

// ─── Re-export organized query hooks ────────────────────
export { useMatters, useMatter, useMatterByNumber, useCreateMatter, useMatterStages, useUpdateMatterStage } from './queries/useMatters';
export { useDeals, usePendingDeals, useDeal, useDealByNumber, useCreateDeal, useUpdateDeal, useAcceptDeal, useRejectDeal } from './queries/useDeals';
export { useBillingItems, useCreateBillingItem, useUpdateBillingItem } from './queries/useBilling';
export { useEscrowPayments, useCreateEscrowPayment, useConfirmEscrowRelease } from './queries/useEscrowPayments';
export { useAuditLog } from './queries/useAuditLog';
export { useAgentDeals, useAgentDeal, useSubmitDeal, useAvailableLawyers } from './queries/useAgentDeals';
export { useProfile, useUpdateProfile, useUpdateKYC } from './queries/useProfile';
export { useClientTransactions, useClientTransaction, useClientDocuments } from './queries/useClientData';

// ─── Properties ─────────────────────────────────────────
export function useProperties() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['properties', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const profileMap = await fetchDirectoryProfileMap(
        (data || []).flatMap((property: any) => [property.listed_by, property.seller_user_id]),
      );

      const imageReferences = [...new Map(
        ((data || [])
          .flatMap((property: any) => property.image_urls || [])
          .map((imageValue: string) => [imageValue, extractStorageObjectPath('properties', imageValue)] as const)
          .filter((entry): entry is readonly [string, string] => Boolean(entry[1])))
          .map(([imageValue, objectPath]) => [imageValue, objectPath]),
      ).entries()];

      const signedUrlEntries = await Promise.all(
        imageReferences.map(async ([imageValue, objectPath]) => {
          const { data: signedUrlData, error: signedUrlError } = await supabase.storage
            .from('properties')
            .createSignedUrl(objectPath, 60 * 60);

          if (signedUrlError || !signedUrlData?.signedUrl) {
            return [imageValue, imageValue] as const;
          }

          return [imageValue, signedUrlData.signedUrl] as const;
        }),
      );

      const signedUrlMap = new Map<string, string>(signedUrlEntries);

      return (data || []).map((property: any) => ({
        ...property,
        listing_agent: property.listed_by ? profileMap.get(property.listed_by) || null : null,
        seller_profile: property.seller_user_id ? profileMap.get(property.seller_user_id) || null : null,
        display_image_urls: (property.image_urls || []).map((imageValue: string) => signedUrlMap.get(imageValue) || imageValue),
      }));
    },
  });
}

// ─── Documents ──────────────────────────────────────────
export function useDocuments(filters?: { matter_id?: string; deal_id?: string }) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['documents', user?.id, filters],
    enabled: !!user,
    queryFn: async () => {
      let query = supabase.from('documents').select('*').order('created_at', { ascending: false });
      if (filters?.matter_id) query = query.eq('matter_id', filters.matter_id);
      if (filters?.deal_id) query = query.eq('deal_id', filters.deal_id);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function usePropertyOffers(propertyId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['property-offers', user?.id, propertyId],
    enabled: !!user && !!propertyId,
    queryFn: async () => {
      let query = supabase
        .from('property_offers')
        .select('*')
        .order('created_at', { ascending: false });

      if (propertyId) {
        query = query.eq('property_id', propertyId);
      }

      const { data, error } = await query;
      const tableMissing = error?.code === '42P01'
        || error?.code === 'PGRST205'
        || error?.message?.toLowerCase().includes('property_offers');
      if (error && tableMissing) return [];
      if (error) throw error;

      const offers = data || [];
      const [profileMap, propertiesResult] = await Promise.all([
        fetchDirectoryProfileMap(offers.flatMap((offer: any) => [offer.buyer_id, offer.owner_id])),
        offers.length > 0
          ? supabase
            .from('properties')
            .select('id, title, address, state, price, status, listed_by, seller_user_id, buyer_user_id, contract_value')
            .in('id', uniqueIds(offers.map((offer: any) => offer.property_id)))
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (propertiesResult.error) throw propertiesResult.error;
      const propertyMap = new Map((propertiesResult.data || []).map((property) => [property.id, property]));

      return offers.map((offer: any) => ({
        ...offer,
        property: propertyMap.get(offer.property_id) || null,
        buyer: offer.buyer_id ? profileMap.get(offer.buyer_id) || null : null,
        owner: offer.owner_id ? profileMap.get(offer.owner_id) || null : null,
      }));
    },
  });
}

// ─── Messages ───────────────────────────────────────────
export function useConversations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['messages', 'conversations', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const messages = data || [];
      const messageIds = messages.map((message) => message.id);
      const [profileMap, attachmentsResult, mattersResult] = await Promise.all([
        fetchMessageProfileMap(messages.flatMap((message) => [message.sender_id, message.recipient_id])),
        messageIds.length > 0
          ? supabase.from('message_attachments').select('*').in('message_id', messageIds)
          : Promise.resolve({ data: [], error: null }),
        uniqueIds(messages.map((message) => message.matter_id)).length > 0
          ? supabase
            .from('matters')
            .select('id, title, matter_number, property_address')
            .in('id', uniqueIds(messages.map((message) => message.matter_id)))
          : Promise.resolve({ data: [], error: null }),
      ]);

      const attachmentsMissing = attachmentsResult.error?.code === '42P01'
        || attachmentsResult.error?.message?.includes('message_attachments');
      if (attachmentsResult.error && !attachmentsMissing) {
        throw attachmentsResult.error;
      }
      if (mattersResult.error) throw mattersResult.error;

      const attachmentsByMessageId = new Map<string, any[]>();
      for (const attachment of attachmentsResult.data || []) {
        const current = attachmentsByMessageId.get(attachment.message_id) || [];
        current.push(attachment);
        attachmentsByMessageId.set(attachment.message_id, current);
      }

      const matterMap = new Map((mattersResult.data || []).map((matter) => [matter.id, matter]));

      return messages.map((message) => ({
        ...message,
        sender: profileMap.get(message.sender_id) || null,
        recipient: profileMap.get(message.recipient_id) || null,
        matter: message.matter_id ? matterMap.get(message.matter_id) || null : null,
        message_attachments: attachmentsByMessageId.get(message.id) || [],
      }));
    },
  });
}

export function useMessageDirectoryProfiles(filters?: {
  search?: string;
  minDealsHandled?: number;
  minYearsExperience?: number;
  state?: string;
  specialty?: string;
}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['message-directory', user?.id, filters],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_directory_profiles');

      if (error) throw error;
      const directoryProfiles = data || [];

      const normalizedSearch = filters?.search?.trim().toLowerCase() || '';
      const normalizedSpecialty = filters?.specialty?.trim().toLowerCase() || '';
      const normalizedState = filters?.state?.trim().toLowerCase() || '';

      return directoryProfiles
        .filter((entry) => {
          const matchesSearch = !normalizedSearch || [
            `${entry.first_name} ${entry.last_name}`,
            entry.organization_name || '',
            entry.professional_bio || '',
            ...(entry.specialties || []),
            ...(entry.service_states || []),
          ].join(' ').toLowerCase().includes(normalizedSearch);

          const matchesDeals = !filters?.minDealsHandled || entry.deals_handled >= filters.minDealsHandled;
          const matchesExperience = !filters?.minYearsExperience || (entry.years_experience || 0) >= filters.minYearsExperience;
          const matchesState = !normalizedState || (entry.service_states || []).some((state) => state.toLowerCase() === normalizedState);
          const matchesSpecialty = !normalizedSpecialty || (entry.specialties || []).some((specialty) => specialty.toLowerCase().includes(normalizedSpecialty));

          return matchesSearch && matchesDeals && matchesExperience && matchesState && matchesSpecialty;
        });
    },
  });
}

export function useSendMessage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload(25, {
    allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'webm', 'mp3', 'm4a', 'wav', 'ogg'],
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'audio/webm',
      'audio/mpeg',
      'audio/mp4',
      'audio/wav',
      'audio/ogg',
    ],
  });
  return useMutation({
    mutationFn: async (message: { content?: string; recipient_id: string; sender_id: string; matter_id?: string; attachments?: File[] }) => {
      const trimmedContent = message.content?.trim() || null;
      const attachments = (message.attachments || []).filter((attachment) => {
        if (!isVoiceNoteAttachment(attachment)) {
          return true;
        }

        return attachment.size > 0;
      });
      const safetyViolation = findRestrictedMessageContent(trimmedContent);

      if (!trimmedContent && attachments.length === 0) {
        throw new Error('Add a message or attachment before sending.');
      }

      if (safetyViolation) {
        throw new Error(safetyViolation);
      }

      await consumeLimitedAction('message_send', 'message');

      const uploadedAttachments = [];
      for (const attachment of attachments) {
        const result = await upload({
          file: attachment,
          name: attachment.name,
          bucket: 'message-files',
          createDocumentRecord: false,
        });

        uploadedAttachments.push({
          file_name: attachment.name,
          file_path: result.filePath,
          public_url: result.publicUrl,
          mime_type: attachment.type || null,
          file_size: attachment.size,
        });
      }

      const { data, error } = await supabase.from('messages').insert({
        content: trimmedContent,
        recipient_id: message.recipient_id,
        sender_id: message.sender_id,
        matter_id: message.matter_id,
      }).select().single();
      if (error) throw error;

      if (uploadedAttachments.length > 0) {
        const attachmentInsert = await supabase.from('message_attachments').insert(
          uploadedAttachments.map((attachment) => ({
            ...attachment,
            message_id: data.id,
            uploaded_by: message.sender_id,
          })) as never,
        );

        const attachmentMissing = attachmentInsert.error?.code === '42P01'
          || attachmentInsert.error?.message?.includes('message_attachments');

        if (attachmentInsert.error && !attachmentMissing) {
          throw attachmentInsert.error;
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count', user?.id] });
    },
  });
}

// ─── Notifications ──────────────────────────────────────
export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (senderId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('recipient_id', user.id)
        .eq('sender_id', senderId)
        .is('read_at', null);

      if (error) throw error;
    },
    onMutate: async (senderId) => {
      if (!user) return undefined;

      const queryKey = ['messages', 'conversations', user.id];
      await queryClient.cancelQueries({ queryKey });
      const previousMessages = queryClient.getQueryData<any[]>(queryKey);
      const nextReadAt = new Date().toISOString();

      queryClient.setQueryData<any[]>(queryKey, (current = []) =>
        current.map((message) =>
          message.recipient_id === user.id && message.sender_id === senderId && !message.read_at
            ? { ...message, read_at: nextReadAt }
            : message,
        ),
      );

      return { previousMessages, queryKey };
    },
    onError: (_error, _senderId, context) => {
      if (context?.previousMessages && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousMessages);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count', user?.id] });
    },
  });
}

export function useLawyerEngagementRequests() {
  const { user, profile } = useAuth();

  return useQuery({
    queryKey: ['lawyer-engagement-requests', user?.id, profile?.role],
    enabled: !!user && profile?.role === 'lawyer',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lawyer_engagement_requests')
        .select('*')
        .eq('lawyer_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const requests = data || [];
      const [profileMap, propertiesResult] = await Promise.all([
        fetchDirectoryProfileMap(requests.map((request: any) => request.requested_by)),
        requests.length > 0
          ? supabase
            .from('properties')
            .select('id, title, address, state, price')
            .in('id', uniqueIds(requests.map((request: any) => request.property_id)))
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (propertiesResult.error) throw propertiesResult.error;
      const propertyMap = new Map((propertiesResult.data || []).map((property) => [property.id, property]));

      return requests.map((request: any) => ({
        ...request,
        property: propertyMap.get(request.property_id) || null,
        requester: request.requested_by ? profileMap.get(request.requested_by) || null : null,
      }));
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (document: { id: string; file_path: string; uploaded_by?: string | null; status?: string | null }) => {
      if (!canDeleteDocument(document, user?.id)) {
        throw new Error('Only the uploader can delete pending or rejected uploads.');
      }

      const storageResult = await supabase.storage.from('documents').remove([document.file_path]);
      if (storageResult.error && !storageResult.error.message?.toLowerCase().includes('not found')) {
        throw storageResult.error;
      }

      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', document.id);

      if (error) throw error;
      return document.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useActivePropertyTransactions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['active-property-transactions', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lawyer_engagement_requests')
        .select('id, created_at, reviewed_at, status, request_message, property_id, requested_by, lawyer_id')
        .eq('status', 'accepted')
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const requests = data || [];
      const [profileMap, propertiesResult] = await Promise.all([
        fetchDirectoryProfileMap(requests.flatMap((request: any) => [request.requested_by, request.lawyer_id])),
        requests.length > 0
          ? supabase
            .from('properties')
            .select('id, title, address, state, status, price, listed_by, seller_user_id, contract_value, active_deal_id, active_matter_id')
            .in('id', uniqueIds(requests.map((request: any) => request.property_id)))
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (propertiesResult.error) throw propertiesResult.error;
      const propertyMap = new Map((propertiesResult.data || []).map((property) => [property.id, property]));

      return requests.map((request: any) => ({
        ...request,
        property: propertyMap.get(request.property_id) || null,
        requester: request.requested_by ? profileMap.get(request.requested_by) || null : null,
        lawyer: request.lawyer_id ? profileMap.get(request.lawyer_id) || null : null,
      })).filter((request: any) => {
        const propertyStatus = (request.property?.status || '').toLowerCase();
        return propertyStatus === 'under_offer' || propertyStatus === 'under-transaction';
      });
    },
  });
}

export function useCreateLawyerEngagementRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      lawyerId,
      propertyId,
      requestMessage,
    }: {
      lawyerId: string;
      propertyId: string;
      requestMessage?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('lawyer_engagement_requests')
        .insert({
          lawyer_id: lawyerId,
          property_id: propertyId,
          requested_by: user.id,
          request_message: requestMessage?.trim() || null,
        })
        .select()
        .single();

      if (error) throw error;

      try {
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: lawyerId,
            title: 'New lawyer request',
            message: 'A property participant has invited you to review and join a transaction.',
            type: 'deal',
            link_to: '/lawyer-dashboard',
          },
        });
      } catch {
        // Non-blocking for the request itself.
      }

      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lawyer-engagement-requests'] });
      queryClient.invalidateQueries({ queryKey: ['active-property-transactions'] });

      void data;
      void variables;
    },
  });
}

export function useReviewLawyerEngagementRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      requestId,
      status,
    }: {
      requestId: string;
      status: 'accepted' | 'rejected';
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('lawyer_engagement_requests')
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('id', requestId)
        .select('id, requested_by, property_id')
        .single();

      if (error) throw error;

      try {
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: data.requested_by,
            title: status === 'accepted' ? 'Lawyer request accepted' : 'Lawyer request declined',
            message:
              status === 'accepted'
                ? 'A lawyer has accepted your property request and your transaction can now continue securely on PropTRUST.'
                : 'A lawyer declined your property request. You can invite another lawyer from the property page.',
            type: 'deal',
            link_to: status === 'accepted' ? `/messages?contact=${user.id}` : '/properties',
          },
        });
      } catch {
        // Non-blocking for the request review itself.
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lawyer-engagement-requests'] });
    },
  });
}

export function useNotifications() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['notifications', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useRealtimeNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .in('id', ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ─── Party Name Lookups (Security Definer) ──────────────
export function useMatterPartyNames(matterId: string | null | undefined) {
  return useQuery({
    queryKey: ['party-names', 'matter', matterId],
    enabled: !!matterId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_matter_party_names', {
        p_matter_id: matterId!,
      });
      if (error) throw error;
      return data as { user_id: string; display_name: string; role: string }[];
    },
  });
}

export function useDealPartyNames(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['party-names', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_deal_party_names', {
        p_deal_id: dealId!,
      });
      if (error) throw error;
      return data as { user_id: string; display_name: string; role: string }[];
    },
  });
}

export function getPartyName(
  parties: { user_id: string; display_name: string }[] | undefined,
  id: string | null
): string {
  if (!id || !parties) return 'Unknown';
  const p = parties.find(p => p.user_id === id);
  return p?.display_name || 'Unknown';
}

// ─── Helpers ────────────────────────────────────────────
export { formatNaira, formatNairaCompact, formatDate, formatRelativeDate } from '@/lib/format';
import { MATTER_STAGE_KEYS } from '@/lib/constants';

export function getStageLabel(stageKey: string | null) {
  const stage = MATTER_STAGE_KEYS.find(s => s.key === (stageKey || ''));
  return stage?.label || stageKey || 'Unknown';
}

// ─── File Upload ────────────────────────────────────────
export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      file, name, category, matter_id, stage_id, deal_id, visibility,
    }: {
      file: File; name: string; category?: string; matter_id?: string;
      stage_id?: string; deal_id?: string; visibility?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const filePath = `${user.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const fileExt = file.name.split('.').pop();
      const { data, error } = await supabase
        .from('documents')
        .insert({
          name, file_path: filePath, file_type: fileExt || null,
          file_size: file.size, category: category || null,
          matter_id: matter_id || null, stage_id: stage_id || null,
          deal_id: deal_id || null, visibility: visibility || 'lawyer-only',
          uploaded_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
